/**
 * @file DesktopKeepBannerHolders.tsx
 * @description The body of the claim banner: who else keeps this artifact.
 * Consumer: `DesktopKeepBanner`, which mounts it only once it has a pin path —
 * `useDesktopPinHolders` opens a live `users` query listener, and this component
 * is the boundary that keeps that listener off a page with nothing to ask about.
 *
 * `User` carries no photo field, so the group is initials. Each avatar's tooltip
 * carries that holder's pin date; the sentence carries the oldest holder's, and
 * `resolveDesktopPinClaim` owns the rule that no total is ever printed.
 *
 * A failed query reads as no holders in the hook, so the banner disappears
 * rather than asserting anything, and it renders nothing while the query is
 * still loading — an empty banner that fills in is worse than one that arrives.
 */

import { FC, useContext } from 'react';
import { Avatar, Tooltip, Typography, theme } from 'antd';

import { useDesktopPinHolders } from '@/use/desktopSync/useDesktopPinHolders';
import { UserContext } from '../../providers/UserContext';
import FlexBox from '../atoms/FlexBox';
import { resolveDesktopPinClaim } from './resolveDesktopPinClaim';

const { Text } = Typography;

export type DesktopKeepBannerHoldersProps = {
    /** Already folded by `resolveDesktopPinPath`. */
    pinPath: string;
    artifactLabel: 'form' | 'record';
};

const DesktopKeepBannerHolders: FC<DesktopKeepBannerHoldersProps> = ({ pinPath, artifactLabel }) => {
    const { token } = theme.useToken();
    const { user } = useContext(UserContext);
    const { holders, loading } = useDesktopPinHolders(pinPath);

    const claim = resolveDesktopPinClaim(holders, user?.uid, artifactLabel);
    if (loading || !claim) {
        return null;
    }

    return (
        <FlexBox
            noGrow
            justifyStart
            gap={10}
            aria-label={`People who keep this ${artifactLabel} on their computer`}
        >
            <Avatar.Group
                size="small"
                max={{ count: 4, popover: { trigger: 'hover', placement: 'top' } }}
            >
                {claim.avatars.map((avatar) => (
                    <Tooltip key={avatar.userId} title={avatar.title}>
                        <Avatar size="small" style={{ backgroundColor: token.colorFillSecondary, color: token.colorText }}>
                            {avatar.initials}
                        </Avatar>
                    </Tooltip>
                ))}
            </Avatar.Group>
            <Text type="secondary">{claim.sentence}</Text>
        </FlexBox>
    );
};

export default DesktopKeepBannerHolders;
